import {
  BarChart3,
  Bot,
  Building2,
  CalendarCheck,
  CalendarDays,
  Inbox,
  KanbanSquare,
  LayoutDashboard,
  LogOut,
  Menu,
  Plus,
  Search,
  Settings,
  ShieldCheck,
  Store,
  UsersRound,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { CommandPalette } from '@/components/layout/CommandPalette';
import { CompanySwitcher } from '@/components/layout/CompanySwitcher';
import { NotificationsBell } from '@/components/layout/NotificationsBell';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { ADMIN_ROLES, MANAGER_ROLES } from '@performance-crm/shared';
import { Button } from '@/components/ui/button';
import { logout } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { cn } from '@/lib/utils';

interface NavItem {
  to: string;
  label: string;
  icon: typeof CalendarCheck;
  end: boolean;
  /** Otras rutas que mantienen esta entrada resaltada. */
  also?: string[];
}

const NAV: NavItem[] = [
  { to: '/', label: 'Mi día', icon: CalendarCheck, end: true },
  // Un solo apartado para los leads (antes «Pipeline»; la ruta /pipeline se mantiene): el tablero por defecto y la
  // tabla como segunda vista; la ficha cuelga de él.
  { to: '/pipeline', label: 'Leads', icon: KanbanSquare, end: true, also: ['/leads'] },
  { to: '/atencion', label: 'Atención del bot', icon: Bot, end: true },
  { to: '/embudo', label: 'Embudo', icon: BarChart3, end: false },
  { to: '/comercial', label: 'Comercial', icon: Store, end: false },
  { to: '/resumen', label: 'Resumen', icon: LayoutDashboard, end: true },
];
const MANAGER_NAV: NavItem[] = [
  { to: '/equipo', label: 'Equipo', icon: UsersRound, end: false },
  { to: '/reportes/calidad', label: 'Calidad de leads', icon: ShieldCheck, end: true },
  { to: '/reportes/gestion-diaria', label: 'Gestión diaria', icon: CalendarDays, end: true },
  { to: '/ingesta', label: 'Ingesta', icon: Inbox, end: false },
];
const ADMIN_NAV: NavItem[] = [
  { to: '/admin', label: 'Administración', icon: Settings, end: false },
];

export function AppShell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearch(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  async function onLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  const isManager = !!user?.memberships.some((m) => MANAGER_ROLES.includes(m.role));
  const isAdmin = !!user?.memberships.some((m) => ADMIN_ROLES.includes(m.role));
  const nav = (
    <nav className="flex flex-col gap-1 p-3">
      {[...NAV, ...(isManager ? MANAGER_NAV : []), ...(isAdmin ? ADMIN_NAV : [])].map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={() => setOpen(false)}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              isActive || item.also?.some((prefix) => location.pathname.startsWith(prefix))
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )
          }
        >
          <item.icon className="h-4 w-4" />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );

  // Bajo el nombre del usuario solo van las empresas visibles (la oculta se administra desde Administración).
  const companies =
    user?.memberships
      .filter((m) => m.companyActive !== false)
      .map((m) => m.companyName)
      .filter((v, i, a) => a.indexOf(v) === i) ?? [];

  return (
    <div className="flex min-h-screen">
      <aside className="bg-card hidden w-60 shrink-0 border-r md:flex md:flex-col">
        <div className="flex h-14 items-center gap-2 border-b px-4 font-semibold">
          <Building2 className="text-primary h-5 w-5" /> Performance CRM
        </div>
        {nav}
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={() => setOpen(false)}>
          <aside className="bg-card h-full w-64" onClick={(e) => e.stopPropagation()}>
            <div className="flex h-14 items-center justify-between border-b px-4 font-semibold">
              Performance CRM
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setOpen(false)}
                aria-label="Cerrar menú"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            {nav}
          </aside>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="bg-card sticky top-0 z-30 flex h-14 items-center justify-between gap-2 border-b px-3 md:px-4">
          <div className="flex min-w-0 items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setOpen(true)}
              aria-label="Abrir menú"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <button
              type="button"
              onClick={() => setSearch(true)}
              className="bg-background text-muted-foreground hover:bg-accent flex h-9 items-center gap-2 rounded-md border px-3 text-sm md:w-72"
              aria-label="Buscar (⌘K)"
            >
              <Search className="h-4 w-4" />
              <span className="hidden md:inline">Buscar lead, teléfono, correo…</span>
              <kbd className="ml-auto hidden rounded border px-1 text-[10px] md:inline">⌘K</kbd>
            </button>
          </div>
          <div className="flex items-center gap-2">
            <CompanySwitcher />
            <ThemeToggle />
            <NotificationsBell />
            <Button asChild size="sm">
              <Link to="/leads/new">
                <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Nuevo lead</span>
              </Link>
            </Button>
            <div className="hidden text-right sm:block">
              <p className="text-sm font-medium leading-tight" data-testid="current-user">
                {user?.fullName}
              </p>
              <p className="text-muted-foreground text-xs" data-testid="user-companies">
                {companies.join(' · ')}
              </p>
            </div>
            <Button variant="ghost" size="icon" onClick={onLogout} aria-label="Cerrar sesión">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
      <CommandPalette open={search} onOpenChange={setSearch} />
    </div>
  );
}
